import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Eye, EyeOff, Copy } from 'lucide-react';
import api from '@/lib/api';
import useLegalConfig from '@/hooks/useLegalConfig';
import { toast } from 'sonner';

export default function SetupWizard() {
  const navigate = useNavigate();
  const { tenant, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);

  // Form data
  const [whatsappConfig, setWhatsappConfig] = useState({
    phone_number_id: '',
    access_token: '',
    verify_token: '',
    phone_number: '',
  });

  const [settings, setSettings] = useState({
    languages_enabled: ['it', 'de', 'en', 'es'],
    default_language: 'it',
    pricing_text: 'Consulta il nostro sito per i dettagli.',
    durations: '30,60,90',
    rules_text: 'Puntualità richiesta. Cancellazioni 24h prima.',
    location_text: 'Posizione centrale. Ti invieremo l\'indirizzo esatto dopo la conferma.',
    welcome_text: 'Ciao! Sono EVA, la tua assistente per prenotazioni. Come posso aiutarti?',
    external_details_link: '',
  });

  const [testMessage, setTestMessage] = useState({
    recipient: '',
    message: 'Ciao! Questo è un messaggio di test da EVA AI.',
  });

  useEffect(() => {
    fetchExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExistingData = async () => {
    try {
      const [whatsappRes, whatsappSetupRes, settingsRes] = await Promise.all([
        api.get('/whatsapp/config'),
        api.get('/whatsapp/setup-info'),
        api.get('/tenant/settings'),
      ]);

      if (whatsappRes.data.configured) {
        setWhatsappConfig(prev => ({
          ...prev,
          phone_number_id: whatsappRes.data.phone_number_id || '',
          verify_token: whatsappSetupRes.data.verify_token || '',
          phone_number: whatsappRes.data.phone_number || '',
        }));
      }

      if (settingsRes.data) {
        setSettings(prev => ({
          ...prev,
          ...settingsRes.data,
          durations: Array.isArray(settingsRes.data.durations) 
            ? settingsRes.data.durations.join(',') 
            : '30,60,90',
        }));
      }
    } catch (error) {
      console.error('Failed to fetch existing data:', error);
    }
  };

  const isInternalPlan = tenant?.plan === 'internal';
  const canProceed = () => {
    if (currentStep === 1 && !isInternalPlan) {
      return tenant?.subscription_status === 'active';
    }
    return true;
  };

  const handleNext = async () => {
    if (currentStep === 2) {
      // Save WhatsApp config
      if (!whatsappConfig.phone_number_id || !whatsappConfig.access_token || !whatsappConfig.verify_token) {
        toast.error(t('wizard.fillRequired'));
        return;
      }
      setLoading(true);
      try {
        await api.put('/whatsapp/config', whatsappConfig);
        toast.success(t('wizard.whatsappSaved'));
        setCurrentStep(3);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('settings.saveError'));
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 3) {
      // Save settings
      setLoading(true);
      try {
        const payload = {
          ...settings,
          durations: [...new Set(
            settings.durations.split(',')
              .map(d => parseInt(d.trim()))
              .filter(d => !isNaN(d) && d > 0)
          )].sort((a, b) => a - b),
        };
        await api.put('/tenant/settings', payload);
        toast.success(t('wizard.settingsSaved'));
        setCurrentStep(4);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('settings.saveError'));
      } finally {
        setLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (isInternalPlan && currentStep === 2) {
      setCurrentStep(1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSendTest = async () => {
    if (!testMessage.recipient || !testMessage.message) {
      toast.error(t('wizard.fillRecipient'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/whatsapp/test', testMessage);
      toast.success(t('wizard.testSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    toast.success(t('wizard.setupComplete'));
    navigate('/dashboard');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BillingStep tenant={tenant} isInternalPlan={isInternalPlan} />;
      case 2:
        return (
          <WhatsAppStep
            config={whatsappConfig}
            setConfig={setWhatsappConfig}
            showToken={showToken}
            setShowToken={setShowToken}
          />
        );
      case 3:
        return <SettingsStep settings={settings} setSettings={setSettings} />;
      case 4:
        return (
          <TestStep
            testMessage={testMessage}
            setTestMessage={setTestMessage}
            onSend={handleSendTest}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  const progress = (currentStep / 4) * 100;
  const effectiveStep = isInternalPlan && currentStep === 1 ? 2 : currentStep;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="wizard-page">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">{t('wizard.title')}</h1>
          <LanguageSelector />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress */}
        <div className="mb-8" data-testid="wizard-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              {t('wizard.stepOf', { step: effectiveStep, total: 4 })}
            </span>
            <span className="text-sm text-slate-500">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} />
        </div>

        {/* Step Content */}
        <Card data-testid={`wizard-step-${currentStep}`}>
          {renderStep()}
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8" data-testid="wizard-navigation">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || (isInternalPlan && currentStep === 2)}
            data-testid="button-back"
          >
            {t('common.back')}
          </Button>
          {currentStep < 4 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              data-testid="button-next"
            >
              {loading ? t('common.saving') : t('common.next')}
            </Button>
          ) : (
            <Button onClick={handleComplete} data-testid="button-complete">
              {t('wizard.completeSetup')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function BillingStep({ tenant, isInternalPlan }) {
  const { config: legalConfig } = useLegalConfig();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleStartCheckout = async () => {
    setLoading(true);
    try {
      const response = await api.post('/billing/checkout');
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(t('wizard.checkoutError'));
      setLoading(false);
    }
  };

  if (isInternalPlan) {
    return (
      <CardContent className="py-12">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('wizard.founderTitle')}</h3>
          <p className="text-slate-600 mb-6">
            {t('wizard.founderDesc')}
          </p>
          <Alert className="border-emerald-200 bg-emerald-50">
            <AlertDescription className="text-emerald-800">
              {t('wizard.founderHint')}
            </AlertDescription>
          </Alert>
        </div>
      </CardContent>
    );
  }

  const isActive = tenant?.subscription_status === 'active';

  return (
    <>
      <CardHeader>
        <CardTitle>{t('wizard.billingTitle')}</CardTitle>
        <CardDescription>{t('wizard.billingDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {legalConfig?.launch?.paid_sales_enabled === false && (
          <Alert className="mb-6 border-amber-200 bg-amber-50" data-testid="private-pilot-billing">
            <AlertDescription className="text-amber-900">{t('wizard.privatePilotBilling')}</AlertDescription>
          </Alert>
        )}
        {isActive ? (
          <Alert className="border-emerald-200 bg-emerald-50" data-testid="alert-subscription-active">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              {t('wizard.subscriptionActive')}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-slate-900">{t('wizard.standardPlan')}</h4>
                  <p className="text-sm text-slate-600">{t('wizard.standardPlanDesc')}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-emerald-600">99 CHF</div>
                  <p className="text-sm text-slate-500">{t('wizard.perMonth')}</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                  {t('wizard.featureWhatsApp')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                  {t('wizard.featureMultilang')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                  {t('wizard.featureBooking')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                  {t('wizard.featureDashboard')}
                </li>
              </ul>
            </div>
            <Button
              className="w-full"
              size="lg"
              onClick={handleStartCheckout}
              disabled={loading || legalConfig?.launch?.paid_sales_enabled === false}
              data-testid="button-start-checkout"
            >
              {legalConfig?.launch?.paid_sales_enabled === false ? t('wizard.privatePilotClosed') : (loading ? t('wizard.activating') : t('wizard.activateSubscription'))}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-4">
              {t('wizard.stripeRedirect')}
            </p>
          </>
        )}
      </CardContent>
    </>
  );
}

function WhatsAppStep({ config, setConfig, showToken, setShowToken }) {
  const { t } = useTranslation();
  const webhookUrl = `${window.location.origin}/api/webhook/whatsapp`;

  const handleChange = (e) => {
    setConfig({ ...config, [e.target.name]: e.target.value });
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success(t('settings.urlCopied'));
  };

  return (
    <>
      <CardHeader>
        <CardTitle>{t('wizard.whatsappTitle')}</CardTitle>
        <CardDescription>
          {t('wizard.whatsappDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert data-testid="alert-webhook-url">
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900 mb-1">{t('wizard.webhookUrl')}</p>
                <code className="text-xs bg-slate-100 px-2 py-1 rounded mono text-slate-700">
                  {webhookUrl}
                </code>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyWebhookUrl}
                data-testid="button-copy-webhook"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="phone_number_id" data-testid="label-phone-number-id">
            {t('wizard.phoneNumberId')} *
          </Label>
          <Input
            id="phone_number_id"
            name="phone_number_id"
            value={config.phone_number_id}
            onChange={handleChange}
            placeholder="123456789012345"
            data-testid="input-phone-number-id"
          />
          <p className="text-xs text-slate-500">
            {t('wizard.phoneNumberIdHint')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="access_token" data-testid="label-access-token">
            {t('wizard.accessToken')} *
          </Label>
          <div className="relative">
            <Input
              id="access_token"
              name="access_token"
              type={showToken ? 'text' : 'password'}
              value={config.access_token}
              onChange={handleChange}
              placeholder="EAAxxxxx..."
              className="pr-10 font-mono"
              data-testid="input-access-token"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowToken(!showToken)}
              data-testid="button-toggle-token"
            >
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="verify_token" data-testid="label-verify-token">
            {t('wizard.verifyToken')} *
          </Label>
          <Input
            id="verify_token"
            name="verify_token"
            value={config.verify_token}
            readOnly
            placeholder="EVA webhook verify token"
            data-testid="input-verify-token"
          />
          <p className="text-xs text-slate-500">
            {t('wizard.verifyTokenHint')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone_number" data-testid="label-phone-number">
            {t('wizard.phoneNumber')} ({t('common.optional')})
          </Label>
          <Input
            id="phone_number"
            name="phone_number"
            value={config.phone_number}
            onChange={handleChange}
            placeholder="+41791234567"
            data-testid="input-phone-number"
          />
        </div>
      </CardContent>
    </>
  );
}

function SettingsStep({ settings, setSettings }) {
  const { t } = useTranslation();
  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  return (
    <>
      <CardHeader>
        <CardTitle>{t('wizard.settingsTitle')}</CardTitle>
        <CardDescription>
          {t('wizard.settingsDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="default_language" data-testid="label-default-language">
            {t('wizard.defaultLanguage')}
          </Label>
          <select
            id="default_language"
            name="default_language"
            value={settings.default_language}
            onChange={handleChange}
            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm"
            data-testid="select-default-language"
          >
            <option value="it">{t('languages.it')}</option>
            <option value="de">{t('languages.de')}</option>
            <option value="en">{t('languages.en')}</option>
            <option value="es">{t('languages.es')}</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="durations" data-testid="label-durations">
            {t('wizard.durations')}
          </Label>
          <Input
            id="durations"
            name="durations"
            value={settings.durations}
            onChange={handleChange}
            placeholder="30,60,90"
            data-testid="input-durations"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="external_details_link" data-testid="label-external-details-link">
            {t('wizard.externalDetailsLink')} *
          </Label>
          <Input
            id="external_details_link"
            name="external_details_link"
            value={settings.external_details_link}
            onChange={handleChange}
            placeholder="https://tuosito.com/dettagli"
            data-testid="input-external-details-link"
          />
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800 text-xs">
              {t('wizard.externalDetailsHint')}
            </AlertDescription>
          </Alert>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pricing_text" data-testid="label-pricing-text">
            {t('wizard.pricingText')}
          </Label>
          <Textarea
            id="pricing_text"
            name="pricing_text"
            value={settings.pricing_text}
            onChange={handleChange}
            rows={2}
            data-testid="textarea-pricing-text"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rules_text" data-testid="label-rules-text">
            {t('wizard.rules')}
          </Label>
          <Textarea
            id="rules_text"
            name="rules_text"
            value={settings.rules_text}
            onChange={handleChange}
            rows={2}
            data-testid="textarea-rules-text"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="location_text" data-testid="label-location-text">
            {t('wizard.locationInfo')}
          </Label>
          <Textarea
            id="location_text"
            name="location_text"
            value={settings.location_text}
            onChange={handleChange}
            rows={2}
            data-testid="textarea-location-text"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="welcome_text" data-testid="label-welcome-text">
            {t('wizard.welcomeMessage')}
          </Label>
          <Textarea
            id="welcome_text"
            name="welcome_text"
            value={settings.welcome_text}
            onChange={handleChange}
            rows={2}
            data-testid="textarea-welcome-text"
          />
        </div>
      </CardContent>
    </>
  );
}

function TestStep({ testMessage, setTestMessage, onSend, loading }) {
  const { t } = useTranslation();
  const handleChange = (e) => {
    setTestMessage({ ...testMessage, [e.target.name]: e.target.value });
  };

  return (
    <>
      <CardHeader>
        <CardTitle>{t('wizard.testTitle')}</CardTitle>
        <CardDescription>
          {t('wizard.testDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="border-emerald-200 bg-emerald-50">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">
            {t('wizard.testAlmostDone')}
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="recipient" data-testid="label-recipient">
            {t('wizard.recipient')}
          </Label>
          <Input
            id="recipient"
            name="recipient"
            value={testMessage.recipient}
            onChange={handleChange}
            placeholder="41791234567"
            data-testid="input-recipient"
          />
          <p className="text-xs text-slate-500">
            {t('wizard.recipientHint')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message" data-testid="label-message">
            {t('wizard.messageLabel')}
          </Label>
          <Textarea
            id="message"
            name="message"
            value={testMessage.message}
            onChange={handleChange}
            rows={4}
            data-testid="textarea-message"
          />
        </div>

        <Button
          className="w-full"
          size="lg"
          onClick={onSend}
          disabled={loading || !testMessage.recipient || !testMessage.message}
          data-testid="button-send-test"
        >
          {loading ? t('wizard.sending') : t('wizard.sendTest')}
        </Button>

        <p className="text-xs text-slate-500 text-center">
          {t('wizard.testCheckPhone')}
        </p>
      </CardContent>
    </>
  );
}
